import { authentication } from "@microsoft/teams-js";

const TOKEN_STORAGE_KEY = "howru.sso.token";

let authToken: string | null = null;
let interceptorInstalled = false;
let originalFetch: typeof fetch | null = null;
let refreshTokenPromise: Promise<string | null> | null = null;

type TokenProvider = () => Promise<string | null>;

let tokenProvider: TokenProvider = () => authentication.getAuthToken();

// Outside Teams the token has to come from a different source, e.g. MSAL in the browser.
export const setTokenProvider = (provider: TokenProvider): void => {
    tokenProvider = provider;
};

const getConfiguredApiBaseUrls = (): string[] => {
    return [process.env.REACT_APP_API_URL]
        .filter((value): value is string => Boolean(value))
        .map((value) => value.toLowerCase());
};

const isLikelyApiPath = (pathname: string): boolean => {
    return pathname.startsWith("/api/") || pathname.startsWith("/oi/") || pathname.startsWith("/files/");
};

const getRequestUrl = (input: RequestInfo | URL): string => {
    if (typeof input === "string") {
        return input;
    }

    if (input instanceof URL) {
        return input.toString();
    }

    return input.url;
};

const shouldAttachAuthorizationHeader = (url: string): boolean => {
    const lowerUrl = url.toLowerCase();
    const configuredBaseUrls = getConfiguredApiBaseUrls();

    if (configuredBaseUrls.some((baseUrl) => lowerUrl.startsWith(baseUrl))) {
        return true;
    }

    try {
        const parsedUrl = new URL(url, window.location.origin);

        if (parsedUrl.origin !== window.location.origin) {
            return false;
        }

        return isLikelyApiPath(parsedUrl.pathname);
    } catch {
        return false;
    }
};

// True from startup until the first token request settles, so early API calls wait instead of going out unauthenticated.
let tokenPending = false;
let tokenWaiters: Array<() => void> = [];
const TOKEN_WAIT_TIMEOUT_MS = 8000;

const waitForAuthToken = async (): Promise<string | null> => {
    const existing = getAuthTokenFromStore();
    if (existing || !tokenPending) {
        return existing;
    }

    await new Promise<void>(resolve => {
        const timer = setTimeout(resolve, TOKEN_WAIT_TIMEOUT_MS);
        tokenWaiters.push(() => { clearTimeout(timer); resolve(); });
    });
    return getAuthTokenFromStore();
};

export const setAuthToken = (token: string | null): void => {
    authToken = token;
    tokenPending = false;
    const waiters = tokenWaiters;
    tokenWaiters = [];
    waiters.forEach(release => release());

    if (token) {
        sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
        sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    }
};

export const getAuthTokenFromStore = (): string | null => {
    if (authToken) {
        return authToken;
    }

    const storedToken = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    if (storedToken) {
        authToken = storedToken;
    }

    return authToken;
};

const getFreshAuthToken = async (): Promise<string | null> => {
    if (refreshTokenPromise) {
        return refreshTokenPromise;
    }

    refreshTokenPromise = (async () => {
        try {
            const refreshedToken = await tokenProvider();
            setAuthToken(refreshedToken);
            return refreshedToken;
        } catch {
            setAuthToken(null);
            return null;
        } finally {
            refreshTokenPromise = null;
        }
    })();

    return refreshTokenPromise;
};

const withAuthorizationHeader = (request: Request, token: string): Request => {
    const headers = new Headers(request.headers);
    headers.set("Authorization", `Bearer ${token}`);
    return new Request(request, { headers });
};

export const installApiAuthInterceptor = (): void => {
    if (interceptorInstalled) {
        return;
    }

    originalFetch = globalThis.fetch.bind(globalThis);
    tokenPending = !getAuthTokenFromStore();

    globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
        const url = getRequestUrl(input);
        let token = getAuthTokenFromStore();
        if (!token && shouldAttachAuthorizationHeader(url)) {
            token = await waitForAuthToken();
        }

        if (!token || !shouldAttachAuthorizationHeader(url)) {
            return originalFetch!(input, init);
        }

        const request = new Request(input, init);
        const requestWithToken = withAuthorizationHeader(request, token);
        const retrySourceRequest = requestWithToken.clone();

        const firstResponse = await originalFetch!(requestWithToken);
        if (firstResponse.status !== 401) {
            return firstResponse;
        }

        const refreshedToken = await getFreshAuthToken();
        if (!refreshedToken || refreshedToken === token) {
            return firstResponse;
        }

        const retryRequest = withAuthorizationHeader(retrySourceRequest, refreshedToken);
        return originalFetch!(retryRequest);
    };

    interceptorInstalled = true;
};
